"use client";

import {useEffect, useState} from "react";
import {usePathname, useRouter} from "next/navigation";
import {SimpleTreeView} from "@mui/x-tree-view";
import {Box, CircularProgress, InputBase, Paper, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomTreeItem from "@/src/components/common/knowledge-base/nav/custom-mui/custom-tree-item/custom-tree-item";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChartSimple, faLightbulb} from "@fortawesome/pro-duotone-svg-icons";
import CardTitleContainer from "@/src/mui-custom/card/card-title-container/card-title-container";
import CardTitleBox from "@/src/mui-custom/card/card-title-box/card-title-box";
import CardTitleDivider from "@/src/mui-custom/card/card-title-divider/card-title-divider";

const renderTreeItems: any = (
    nodes: any[],
    handleSelect: any,
    selectedSlug: any,
    depth = 0,
    isSearchActive = false
) => {
    if (isSearchActive) {
      return nodes.map((node) => {
        if (node.children?.length > 0) {
          return renderTreeItems(node.children, handleSelect, selectedSlug, 0, isSearchActive);
        }
        return (
          <CustomTreeItem
              key={node.slug}
              itemId={node.slug}
              data-id={node.slug}
              selectedSlug={selectedSlug}
              depth={depth}
              label={
                  <Box
                      sx={{display: "flex", alignItems: "center", p: 0, color: (theme) => theme.palette.primary.main}}>
                      <Typography variant="body2">{node.name}</Typography>
                  </Box>
              }
              aria-selected={node.slug === selectedSlug}
              onClick={() => {
                  if (node.slug !== selectedSlug && !node.is_parent) handleSelect(node.slug);
              }}
          >
              {node.children?.length > 0 &&
                  renderTreeItems(node.children, handleSelect, selectedSlug, depth + 1)}
          </CustomTreeItem>
        )
      });
    }
    return nodes.map((node) => (
        <CustomTreeItem
            key={node.slug}
            itemId={node.slug}
            data-id={node.slug}
            selectedSlug={selectedSlug}
            depth={depth}
            label={
                <Box
                    sx={{display: "flex", alignItems: "center", p: 0, color: (theme) => theme.palette.primary.main}}>
                    <Typography variant="body2">{node.name}</Typography>
                </Box>
            }
            aria-selected={node.slug === selectedSlug}
            onClick={() => {
                if (node.slug !== selectedSlug && !node.is_parent) handleSelect(node.slug);
            }}
        >
            {node.children?.length > 0 &&
                renderTreeItems(node.children, handleSelect, selectedSlug, depth + 1)}
        </CustomTreeItem>
    ));
};

export default function KbNav({kbTree, selectedSlug, setSelectedSlug}: {
    kbTree: any,
    selectedSlug: string,
    setSelectedSlug: any
}) {
    const router = useRouter();
    const pathname = usePathname();
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredNodes, setFilteredNodes] = useState(kbTree.categories_tree);
    const [loadingTree, setLoadingTree] = useState(false);

    useEffect(() => {
        const slug = window.location.hash.substring(1);
        if (slug) setSelectedSlug(slug);
    }, [pathname]);

    const handleSelect = (slug: string) => {
        if (slug === selectedSlug) return;
        router.push(`${pathname}#${slug}`);
        setSelectedSlug(slug);
    };

    const searchKnowledgebase = async (searchQuery: string): Promise<any[]> => {
        setLoadingTree(true);
        try {
            const response = await fetch(`/api/kb/search?query=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message);
            }
            setLoadingTree(false);
            return data.data;
        } catch (error) {
            console.error('Search failed:', error);
            setLoadingTree(false);
            return [];
        }
    };

    const filterTree = async (nodes: any[], searchQuery: string): Promise<any[]> => {

        if (!searchQuery.trim()) {
            return nodes;
        }

        const searchResults = await searchKnowledgebase(searchQuery);
        const matchedSlugs = new Set(searchResults.map(result => result.slug));

        const filterNode = (node: any): any => {
            // Check if current node matches
            if (matchedSlugs.has(node.slug)) {
                return node;
            }
            if (node.children?.length) {
                const filteredChildren = node.children
                    .map(filterNode)
                    .filter(Boolean);

                if (filteredChildren.length > 0) {
                    return { ...node, children: filteredChildren };
                }
            }

            return null;
        };
        return nodes.map(filterNode).filter(Boolean);
    };

    // Update filtered nodes whenever search query changes
    useEffect(() => {
        const updateFilteredNodes = async () => {
            const filtered = await filterTree(kbTree.categories_tree, searchQuery);
            setFilteredNodes(filtered);
        };

        updateFilteredNodes();
    }, [searchQuery, kbTree.categories_tree]);

    return (
        <Box sx={{minHeight: "300px", display: "flex", flexDirection: "column"}}>
            <Typography variant={"fs3"}
                        component={"h2"}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: .3,
                            color: '#2c9794',
                        }}>
                <FontAwesomeIcon icon={faLightbulb}/>
                <Typography variant={"fs3"}
                            component={"p"}
                >Instructions</Typography>
            </Typography>
            <Paper
                sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                    py: 0.5,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 0,
                    my: 1
                }}
            >
                <SearchIcon sx={{color: "#777", mr: 1}}/>
                <InputBase
                    placeholder="Search..."
                    fullWidth
                    sx={{fontSize: "0.9rem"}}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </Paper>
            <CardTitleContainer>
                <CardTitleBox>
                    <FontAwesomeIcon icon={faChartSimple} className={'icon'} size={"xs"}/>
                    <Typography variant={"fs6"}>
                        Select One
                    </Typography>
                </CardTitleBox>
                <CardTitleDivider/>
            </CardTitleContainer>
            {!loadingTree && (
                <SimpleTreeView
                    slots={{
                        expandIcon: FolderIcon,
                        collapseIcon: FolderOpenIcon,
                        endIcon: DescriptionIcon,
                    }}
                    sx={{padding: 0, flexGrow: 1}}
                    multiSelect={true}>
                    {renderTreeItems(filteredNodes, handleSelect, selectedSlug, 0, !!searchQuery)}
                  </SimpleTreeView>
            )}
            {loadingTree && (
                <CircularProgress sx={{margin: 'auto'}}/>
            )}
        </Box>
    );
}
